import type React from 'react';
import { useEffect } from 'react';
import { Box, Button, ButtonGroup, Heading, Paragraph } from '@zillow/constellation-10';
import { useHistory } from 'react-router-dom';
import CreditTrackingCard from './components/CreditTrackingCard';
import RentReportingHistory from './components/RentReportingHistory';
import { CREDIT_REPORTING_FAQ_URL } from '../../constants/rentReportingConstants';
import type {
  CreditTrackingInterface,
  PaymentReportHistoryInterface,
  PaymentReportingInterface,
  RenterTransactionProps,
} from '../../models/Types';
import paths from '@renter-hub/pathsManifest';

interface RentReportingPageProps {
  onReportingOptOut: () => void;
  autoOpenCreditTrackingEnroll: boolean;
  transactions: Array<RenterTransactionProps>;
  paymentReporting?: PaymentReportingInterface;
  paymentReportHistory?: PaymentReportHistoryInterface;
}

const RentReportingPage: React.FC<RentReportingPageProps> = ({
  onReportingOptOut,
  autoOpenCreditTrackingEnroll,
  transactions,
  paymentReporting,
  paymentReportHistory,
}) => {
  const history = useHistory();

  const { enrollmentStatus = '' } = (paymentReporting?.creditTracking as CreditTrackingInterface) ?? {};
  const { optInDate: reportingOptInDate = '' } = paymentReporting ?? {};

  const optInDateAsDate = reportingOptInDate ? new Date(reportingOptInDate) : null;
  const month = optInDateAsDate?.toLocaleString('default', { month: 'long' });
  const year = optInDateAsDate?.getFullYear();

  useEffect(() => {
    if (autoOpenCreditTrackingEnroll && enrollmentStatus !== 'OPTED_OUT') {
      history.push(paths.baseUrl + paths.paymentReportingOptInPage);
    }
  }, [autoOpenCreditTrackingEnroll, enrollmentStatus, history]);

  return (
    <Box css={{ paddingTop: 'looser' }}>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'tighter',
          paddingBottom: 'default',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: 'default',
          },
        }}
      >
        <Box>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'tighter' }}>
            Rent reporting
          </Heading>
          {month != null && year != null && (
            <Paragraph>
              We’ve been reporting your on-time rent payments since {month} {year}.
            </Paragraph>
          )}
        </Box>
        <Box
          css={{
            '@media (max-width: 768px)': {
              width: '100%',
            },
          }}
        >
          <ButtonGroup
            aria-label="Rent reporting actions"
            css={{
              '@media (max-width: 768px)': {
                display: 'flex',
                gap: 'tighter',
              },
            }}
          >
            <Button
              emphasis="outlined"
              tone="brand"
              asChild
              css={{
                '@media (max-width: 768px)': {
                  flex: '1 1 50%',
                },
              }}
            >
              <a
                href={CREDIT_REPORTING_FAQ_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                View FAQs
              </a>
            </Button>
            <Button
              emphasis="outlined"
              tone="neutral"
              onClick={onReportingOptOut}
              css={{
                '@media (max-width: 768px)': {
                  flex: '1 1 50%',
                },
              }}
            >
              Opt out
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
      {enrollmentStatus !== 'OPTED_OUT' && (
        <CreditTrackingCard creditTracking={paymentReporting?.creditTracking as CreditTrackingInterface} />
      )}
      <RentReportingHistory transactions={transactions} paymentReportHistory={paymentReportHistory} />
    </Box>
  );
};

export default RentReportingPage;
